import { Flex, Button, Input, FileButton, Box } from '@mantine/core';
import { IconChevronDown, IconChevronRight, IconPaperclip, IconX } from '@tabler/icons-react';
import React, { useRef, useState } from 'react';
import theme from 'theme';
import useLang from 'lang';
import SimpleLayout from 'layouts/SimpleLayout';
import H2SemiBold from 'theme/components/Typography/H2SemiBold';
import { HELPDESK_PAGE_PATH } from 'routes/paths';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import ModalLocation from 'components/ModalLocation';
import ModalConfirmation from 'components/ModalConfirmation';
// import { useApi } from 'api/api-context';
import panic from 'panic';

/**
 * Ticket page
 */
const Ticket: React.FC = () => {
  const { _t } = useLang();
  // const { getAction } = useApi();
  const [modalOpened, setModalOpened] = useState(false);
  // const [modalSecondOpened , setSecondModalOpened] = useState(false);
  const [requestModalOpened, setRequestModalOpened] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [radioValue, setRadioValue] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const resetRef = useRef<() => void>(null);

  /**
   * Handle file upload
   */
  const handleFileUpload = (uploadedFiles: File[]) => {
    if (uploadedFiles) {
      setFiles((prev) => [...prev, ...uploadedFiles]);
    }
  };

  /**
   * Clear all files
   */
  const clearAllFiles = () => {
    setFiles([]);
    resetRef.current?.();
  };

  /**
   * Remove a specific file
   */
  const removeFile = (fileToRemove: File) => {
    setFiles(files.filter((file) => file !== fileToRemove));
  };

  /**
   * Get file type icon
   */
  const getFileTypeIcon = (file: File) => {
    const fileType = file.type.split('/')[0];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    if (fileType === 'image') {
      return <IconPaperclip size="1.5rem" color={theme.colors?.blue?.[6]} />;
    } else if (fileType === 'video') {
      return <IconPaperclip size="1.5rem" color={theme.colors?.red?.[6]} />;
    } else if (fileExtension === 'pdf') {
      return <IconPaperclip size="1.5rem" color={theme.colors?.orange?.[6]} />;
    }

    return <IconPaperclip size="1.5rem" />;
  };

  /**
   * Get file preview
   */
  const getFilePreview = (file: File) => {
    const fileType = file.type.split('/')[0];

    if (fileType === 'image') {
      return (
        <Box
          w="100px"
          h="100px"
          style={{
            overflow: 'hidden',
            borderRadius: '4px',
            border: `1px solid ${theme.colors?.gray?.[3]}`,
          }}
        >
          <img
            src={URL.createObjectURL(file)}
            alt={file.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </Box>
      );
    }

    return null;
  };
  const navigate = useNavigate();

  /**
   * Open Modal
   */
  const handleOpenModal = () => {
    setModalOpened(true);
  };

  /**
   * Close modal
   */
  const handleCloseModal = () => {
    setModalOpened(false);
  };

  /**
   * Open Second Modal
   */
  // const handleOpenSecondModal = () => {
  //   setSecondModalOpened(true);
  // };

  /**
   * Close Second Modal
   */
  // const handleCloseSecondModal = () => {
  //   setSecondModalOpened(false);
  // };

  /**
   * Open Send request model
   */
  const handleOpenRequestModal = () => {
    setRequestModalOpened(true);
  };

  /**
   * Close Send request model
   */
  const handleCloseRequestModal = () => {
    setRequestModalOpened(false);
  };

  /**
   * Handle Radio Change
   */
  const handleRadioChange = (value: string, label: string) => {
    setRadioValue(value);
    setSelectedLocation(label);
    setModalOpened(false);
  };

  /**
   * Handle Submit
   */
  const handleSubmit = async () => {
    if (!title.trim()) {
      notifications.show({
        title: _t('Error'),
        message: _t('Please enter a title for the problem'),
        color: 'red',
      });
      return;
    }

    if (!radioValue.trim()) {
      notifications.show({
        title: _t('Error'),
        message: _t('Please select a location for the problem'),
        color: 'red',
      });
      return;
    }

    try {
      setIsSubmitting(true);
      // console.log('Starting job creation with data:', { title, description, location: selectedLocation });

      // const createJob = getAction('CreateJob');

      // Prepare files array for API
      // const fileList = files.map((file) => {
      //   // console.log(`Adding file to request: ${file.name} (${file.size} bytes)`);
      //   return file;
      // });

      // Prepare payload for API
      // const payload = {
      //   name: title, // Using title as name since it's required
      //   description,
      //   targetObjectId: Number(radioValue),
      // };

      // console.log('Sending create job request with payload:', payload);
      // const response = await createJob({ payload });
      // console.log('Create job response:', response);

      navigate(HELPDESK_PAGE_PATH.original);
      notifications.show({
        title: _t('Your request has been successfully entered.'),
        message: '',
        color: 'green',
      });
    } catch (error) {
      // console.error('Error creating job:', error);
      notifications.show({
        title: _t('Error'),
        message: _t('There was an error submitting your request. Please try again.'),
        color: 'red',
      });
      panic(error instanceof Error ? error : new Error(String(error)));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SimpleLayout>
        <Flex p="1rem" gap="1.5rem" direction="column">
          <H2SemiBold color={theme.black}>{_t('Information')}</H2SemiBold>
          <Input.Wrapper label={_t('The title of the problem')} required>
            <Input
              variant="primary"
              placeholder={_t('The title of the problem')}
              value={title}
              onChange={(e) => setTitle(e.currentTarget.value)}
            />
          </Input.Wrapper>
          <Input.Wrapper label={_t('Description of the problem')}>
            <Input
              variant="primary"
              placeholder={_t('Description of the problem')}
              value={description}
              onChange={(e) => setDescription(e.currentTarget.value)}
            />
          </Input.Wrapper>
          <Input.Wrapper label={_t('Location')}>
            <Input
              variant="primary"
              placeholder={_t('Choose')}
              onClick={handleOpenModal}
              value={selectedLocation}
              rightSection={<IconChevronDown />}
            />
          </Input.Wrapper>
          <Flex justify="space-between" align="center">
            <H2SemiBold color={theme.black}>{_t('Files')}</H2SemiBold>
            <Flex gap={'0.5rem'}>
              <FileButton
                resetRef={resetRef}
                onChange={handleFileUpload}
                accept="image/*,video/*,application/pdf"
                multiple
              >
                {(props) => (
                  <Button {...props} variant="primary" rightSection={<IconPaperclip size="1.5rem" />}>
                    {_t('Add files')}
                  </Button>
                )}
              </FileButton>
              <Button disabled={files.length === 0} color="red" onClick={clearAllFiles} variant="secondary">
                {_t('Clear all')}
              </Button>
            </Flex>
          </Flex>

          {files.length > 0 && (
            <Box
              p="1rem"
              style={{
                border: `1px solid ${theme.colors?.gray?.[3]}`,
                borderRadius: '8px',
                backgroundColor: theme.colors?.gray?.[0],
              }}
            >
              <Flex direction="column" gap="0.75rem">
                {files.map((file, index) => (
                  <Flex
                    key={index}
                    justify="space-between"
                    align="center"
                    p="0.5rem"
                    style={{
                      borderRadius: '4px',
                      backgroundColor: theme.white,
                      border: `1px solid ${theme.colors?.gray?.[2]}`,
                    }}
                  >
                    <Flex gap="1rem" align="center" style={{ flex: 1, minWidth: 0 }}>
                      {getFilePreview(file) || getFileTypeIcon(file)}
                      <Flex direction="column" style={{ minWidth: 0, flex: 1 }}>
                        <Box
                          fw={600}
                          style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',
                          }}
                        >
                          {file.name}
                        </Box>
                        <Box fz="xs" c="dimmed">
                          {(file.size / 1024).toFixed(1)} KB
                        </Box>
                      </Flex>
                    </Flex>
                    <Button variant="subtle" color="red" p={0} onClick={() => removeFile(file)}>
                      <IconX size="1.25rem" />
                    </Button>
                  </Flex>
                ))}
              </Flex>
            </Box>
          )}

          <Flex justify="space-between" gap="1rem">
            <Button
              variant="secondary"
              w="100%"
              size="lg"
              leftSection={<IconX size="1.5rem" />}
              onClick={() => navigate(HELPDESK_PAGE_PATH.original)}
            >
              {_t('Cancel')}
            </Button>
            <Button
              variant="primary"
              w="100%"
              size="lg"
              rightSection={<IconChevronRight size="1.5rem" />}
              onClick={handleOpenRequestModal}
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {_t('Send')}
            </Button>
          </Flex>
        </Flex>
      </SimpleLayout>
      <ModalLocation
        modalOpened={modalOpened}
        handleCloseModal={handleCloseModal}
        radioValue={radioValue}
        handleRadioChange={handleRadioChange}
      />
      {/*<ModalFile secondModalOpened={secondModalOpened} handleCloseSecondModal={handleCloseSecondModal} />*/}
      <ModalConfirmation
        requestModalOpened={requestModalOpened}
        handleCloseRequestModal={handleCloseRequestModal}
        handleSubmit={handleSubmit}
        title={_t('Do you really want to send a request?')}
        cancelButtonText={_t('Cancel')}
        submitButtonText={_t('Yes, send')}
        loading={isSubmitting}
      />
    </>
  );
};

export default Ticket;
